import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { fmtNumber } from "@/components/Amount";   // Batch 11 · B7: 2 decimals everywhere
import SignOffBar from "@/components/SignOffBar";   // Batch 11 · A2
import { WS_OPEN_PARAM } from "@/lib/worksheetLinks";
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import FullScreenDialog from "@/components/FullScreenDialog";
import DupGroupResolve from "@/components/DupGroupResolve";
import DateInput from "@/components/DateInput";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/dt";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Ref from "@/components/Ref";
import DownloadTemplateButton from "@/components/DownloadTemplateButton";
import { fmtDate } from "@/lib/dt";
import { asArray } from "@/lib/asArray";
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)
import SaveCancelBar, { SCB_TEXT } from "@/components/SaveCancelBar";   // Batch 28 · Follow-up 1 · F2
import { useFormDraft } from "@/lib/useDrafts";
import { confirmDiscard } from "@/lib/unsavedChanges";
import InventoryFloorRows from "@/components/InventoryFloorRows";   // Step 11c-3 · find 28
import { jobErrorText } from "@/lib/jobErrorText";   // Step 11d · A2: a failed job's error in the screen's language

/**
 * Batch C · Round C2 · W6 — Inventory count worksheet dialog.
 * K5 Fix List — every spec section is on screen, plain in EN + AR:
 * count-at-FS-date branch, date picker, mgmt-instructions checklist,
 * observation notes + condition, test-counts table (floor-to-sheet
 * rows editable, sheet-to-floor rows from Sampling), final records
 * with explained difference, third-party inventory branch,
 * attendance / alt-procedures, working upload control, link to 314(a).
 */
export default function InventoryCountWorksheetDialog() {
  const { currentEngId } = useEngagement();
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const ref = params.get(WS_OPEN_PARAM.inventory_count);
  const [doc, setDoc] = useState(null);
  const [viewRows, setViewRows] = useState(null);
  const listCols = useResizableColumns({ storageKey: "inv-listing-rows", defaults: [56, 130, 320, 160, 110, 120, 130], mins: [40, 90, 140, 100, 90, 96, 100] });

  useEffect(() => {
    if (!ref || !currentEngId) { setDoc(null); return; }
    api.get(`/engagements/${currentEngId}/inventory-count/ref/${ref}`, { _silent: true })
      .then((r) => setDoc(r.data)).catch(() => setDoc(null));
  }, [ref, currentEngId]);

  const fileInputRef = useRef(null);
  // Batch 28 · Follow-up 1 · F2 (a): the worksheet's sections are a page-form draft — nothing is written while typing; Save sends
  // the changed sections in ONE PATCH; Cancel puts the saved values back (the inputs re-mount); Mark complete and Sign wait
  // until saved; closing the dialog asks first. Add floor-to-sheet row, the listing upload / remove / view, duplicate
  // resolve and Send to Sampling stay immediate actions.
  const fd = useFormDraft(doc);
  const [formKey, setFormKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [addingFloor, setAddingFloor] = useState(false);   // Step 11c-3 · find 28
  useEffect(() => { setAddingFloor(false); }, [ref, currentEngId]);
  if (!ref) return null;
  const close = async () => {
    if (!(await confirmDiscard(fd.dirty, lang))) return;
    const p = new URLSearchParams(params); p.delete(WS_OPEN_PARAM.inventory_count); setParams(p, { replace: true });
  };
  const patch = async (body) => {
    try { const r = await api.patch(`/engagements/${currentEngId}/inventory-count/ref/${ref}`, body); setDoc(r.data); return true; }
    catch (e) { toast.error((lang === "ar" && e?.response?.data?.detail_ar) || toErrMsg(e, "Save failed")); return false; }   // Batch 31 · item 28 (M104)
  };
  const draftSet = (k, v) => fd.set(k, v);
  const patchIn = (section, fields) =>
    fd.set(section, { ...((fd.value || {})[section] || {}), ...fields });
  const saveForm = async () => {
    if (!fd.dirty) return;
    setSaving(true);
    try { if (await patch(fd.patch)) { fd.reset(); toast.success(SCB_TEXT[lang === "ar" ? "ar" : "en"].savedOk); } }
    finally { setSaving(false); }
  };
  const cancelForm = () => { fd.reset(); setFormKey((k) => k + 1); };
  // Step 11c-3 · find 28: "Add floor-to-sheet row" opens a new, editable row (saved with its own Save; never an empty row)
  const addFloorRow = () => setAddingFloor(true);
  const reloadDoc = async () => {
    const r = await api.get(`/engagements/${currentEngId}/inventory-count/ref/${ref}`);
    setDoc(r.data);
  };
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      // Q1 fix — backend expects JSON base64 (like the Completeness
      // upload), not multipart. FormData produced a Pydantic 422 that
      // surfaced as "Request failed with status code 500" in axios.
      const buf = await f.arrayBuffer();
      let bin = ""; const bytes = new Uint8Array(buf); const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) {
        bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
      }
      const data_base64 = btoa(bin);
      if (doc?.is_voided) { toast.error(t("inv6.voidedReadOnly")); return; }   // Batch 3 · R69
      const r = await api.post(`/engagements/${currentEngId}/inventory-count/ref/${ref}/upload-listing`,
        { filename: f.name, data_base64 });
      // Batch 3 · R71 — the listing is parsed by a background job: wait for it before re-reading,
      // otherwise the dialog shows the pre-job state ("0 file(s) · 0 rows") until reopened.
      const jid = r.data?.job_id;
      let jobFailed = false;
      if (jid) {
        for (let i = 0; i < 60; i += 1) {
          await new Promise((res) => setTimeout(res, 1500));
          try {
            const j = await api.get(`/jobs/${jid}`, { _silent: true });
            const st = j.data?.status;
            if (st === "done") break;
            if (st === "failed") { jobFailed = true; toast.error(jobErrorText(j.data, lang, t, t("inv6.uploadFailed"))); break; }
          } catch { /* keep polling */ }
        }
      }
      if (!jobFailed) toast.success(t("inv6.uploadOk"));
      const fresh = await api.get(`/engagements/${currentEngId}/inventory-count/ref/${ref}`);
      setDoc(fresh.data);
    } catch (err) { toast.error(toErrMsg(err, "Upload failed")); }
    finally { e.target.value = ""; }
  };

  const gate = doc?.can_mark_complete || { ok: false, reasons: [] };
  const dv = fd.value || {};
  const cd = dv.count_details || {};
  const mi = dv.management_instructions || {};
  const cond = dv.condition || {};
  const tc = doc?.test_counts || {};
  const fr = dv.final_records || {};
  const tp = dv.third_party || {};
  const att = dv.attendance || {};
  const YN = (val, on, tid) => (
    <select className="h-6 text-xs" value={val === null || val === undefined ? "" : String(val)}
             onChange={(e) => on(e.target.value === "true" ? true : e.target.value === "false" ? false : null)}
             data-testid={tid}>
      <option value="">—</option>
      <option value="true">{t("common.yes")}</option>
      <option value="false">{t("common.no")}</option>
    </select>
  );

  return (
    <FullScreenDialog open={true} onClose={close} testid="inv-count-dialog"
                      title={<>{t("inv6.title")} — <Ref>{ref}</Ref></>}>
        {!doc ? (
          <div data-testid="inv-loading">{t("common.loading")}</div>
        ) : (
          <div className="space-y-4 text-xs" key={formKey}>
            <SignOffBar itemType="inventory_count" itemKey={ref} refreshKey={doc.updated_at} saveFirst={fd.dirty}
                        onChange={() => api.get(`/engagements/${currentEngId}/inventory-count/ref/${ref}`, { _silent: true }).then((r) => setDoc(r.data)).catch(() => {})} />
            {/* Count details + Count at FS date */}
            <section className="rounded border p-3" data-testid="inv-count-details">
              <div className="mb-1 text-sm font-semibold">{t("inv6.section.count_details")}</div>
              <div className="grid gap-2 md:grid-cols-2">
                <div>
                  <Label>{t("inv6.countAtFsDate")}</Label>
                  {YN(cd.count_at_fs_date,
                     (v) => patchIn("count_details", { count_at_fs_date: v }),
                     "inv-count-at-fs-date")}
                </div>
                <div>
                  <Label>{t("inv6.countDate")}</Label>
                  <DateInput data-testid="inv-count-date" value={cd.count_date || ""}
                              onChange={(v) => patchIn("count_details", { count_date: v })} />
                </div>
                <div><Label>{t("inv6.countedBy")}</Label>
                  <Input data-testid="inv-counted-by" defaultValue={cd.counted_by || ""}
                    onChange={(e) => patchIn("count_details", { counted_by: e.target.value })} /></div>
                <div><Label>{t("inv6.locations")}</Label>
                  <Input data-testid="inv-locations" defaultValue={asArray(cd.locations).join(", ")}
                    onChange={(e) => patchIn("count_details", { locations: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></div>
              </div>
              {cd.count_at_fs_date === false && (
                <div className="mt-2" data-testid="inv-movements-section">
                  <Label>{t("inv6.movements")}</Label>
                  <Textarea data-testid="inv-movements" defaultValue={cd.movements_note || ""}
                             onChange={(e) => patchIn("count_details", { movements_note: e.target.value })} />
                </div>
              )}
              <div className="mt-2 text-xs">
                <Link to={`/head-folders?open=1201-06`} className="text-blue-700 underline" data-testid="inv-link-314a">
                  {t("inv6.linkTo314a")}
                </Link>
              </div>
            </section>

            {/* Mgmt instructions checklist */}
            <section className="rounded border p-3" data-testid="inv-mgmt">
              <div className="mb-1 text-sm font-semibold">{t("inv6.section.mgmt_instructions")}</div>
              <div className="grid gap-2 md:grid-cols-3">
                {["coverage", "cut_off", "identification", "recording", "custody", "damaged_notes"].map((k) => (
                  <div key={k}>
                    <Label className="text-xs">{t(`inv6.mgmt.${k}`)}</Label>
                    {YN((mi.checklist || {})[k],
                       (v) => patchIn("management_instructions", { checklist: { ...(mi.checklist || {}), [k]: v } }),
                       `inv-mgmt-${k}`)}
                  </div>
                ))}
              </div>
              <div className="mt-2">
                <Label>{t("inv6.mgmt.note")}</Label>
                <Textarea data-testid="inv-mgmt-note" defaultValue={mi.note || ""}
                           onChange={(e) => patchIn("management_instructions", { note: e.target.value })} />
              </div>
              <label className="mt-2 inline-flex items-center text-xs">
                <input type="checkbox" data-testid="inv-mgmt-eval"
                  checked={!!mi.evaluated}
                  onChange={(e) => patchIn("management_instructions", { evaluated: e.target.checked })} />
                &nbsp;{t("inv6.mgmt.evaluatedFlag")}
              </label>
            </section>

            {/* Observation + condition */}
            <section className="rounded border p-3" data-testid="inv-observation">
              <div className="mb-1 text-sm font-semibold">{t("inv6.section.observation")}</div>
              <Textarea data-testid="inv-observation-notes" defaultValue={dv.observation_notes || ""}
                         onChange={(e) => draftSet("observation_notes", e.target.value)} />
              <div className="mt-2 grid gap-2 md:grid-cols-3">
                <div><Label>{t("inv6.damaged")}</Label>
                  <Input data-testid="inv-cond-damaged" defaultValue={cond.damaged || ""}
                          onChange={(e) => patchIn("condition", { damaged: e.target.value })} /></div>
                <div><Label>{t("inv6.obsolete")}</Label>
                  <Input data-testid="inv-cond-obsolete" defaultValue={cond.obsolete || ""}
                          onChange={(e) => patchIn("condition", { obsolete: e.target.value })} /></div>
                <div><Label>{t("inv6.slowMoving")}</Label>
                  <Input data-testid="inv-cond-slow" defaultValue={cond.slow_moving || ""}
                          onChange={(e) => patchIn("condition", { slow_moving: e.target.value })} /></div>
              </div>
            </section>

            {/* Test counts */}
            <section className="rounded border p-3" data-testid="inv-test-counts">
              <div className="mb-1 flex items-center justify-between">
                <div className="text-sm font-semibold">{t("inv6.section.test_counts")}</div>
                <Button size="sm" variant="outline" onClick={addFloorRow} data-testid="inv-add-floor-row"
                        disabled={addingFloor || !!doc?.is_voided || !!doc?.completed}>
                  {t("inv6.addFloorRow")}
                </Button>
              </div>
              <TestCountsTable rows={tc.sheet_to_floor_rows || []} kind="sheet_to_floor" t={t} testidPrefix="inv-s2f" />
              <InventoryFloorRows engId={currentEngId} wsRef={ref} rows={tc.floor_to_sheet_rows || []} adding={addingFloor}
                                  setAdding={setAddingFloor} onChanged={reloadDoc} disabled={!!doc?.is_voided || !!doc?.completed}
                                  t={t} lang={lang} />
            </section>

            {/* Final records */}
            <section className="rounded border p-3" data-testid="inv-final">
              <div className="mb-1 text-sm font-semibold">{t("inv6.section.final_records")}</div>
              <div className="grid gap-2 md:grid-cols-3">
                <div><Label>{t("inv6.finalListing")}</Label>
                  <Input type="number" data-testid="inv-final-listing"
                    defaultValue={fr.final_listing_total ?? ""}
                    onChange={(e) => patchIn("final_records", { final_listing_total: e.target.value === "" ? null : parseFloat(e.target.value) })} /></div>
                <div><Label>{t("inv6.countResults")}</Label>
                  <Input type="number" data-testid="inv-count-results"
                    defaultValue={fr.count_results_total ?? ""}
                    onChange={(e) => patchIn("final_records", { count_results_total: e.target.value === "" ? null : parseFloat(e.target.value) })} /></div>
                <div><Label>{t("inv6.tbTotal")}</Label>
                  <Input type="number" data-testid="inv-tb-total"
                    defaultValue={fr.tb_total ?? ""}
                    onChange={(e) => patchIn("final_records", { tb_total: e.target.value === "" ? null : parseFloat(e.target.value) })} /></div>
                <div className="col-span-3"><Label>{t("inv6.diffExplain")}</Label>
                  <Textarea data-testid="inv-diff-explain" defaultValue={fr.difference_explanation || ""}
                    onChange={(e) => patchIn("final_records", { difference_explanation: e.target.value })} /></div>
              </div>
            </section>

            {/* Third-party inventory */}
            <section className="rounded border p-3" data-testid="inv-third-party">
              <div className="mb-1 text-sm font-semibold">{t("inv6.section.third_party")}</div>
              <div className="flex items-center gap-3">
                <Label>{t("inv6.thirdPartyHeld")}</Label>
                {YN(tp.held === true ? true : tp.held === false ? false : null,
                    (v) => patchIn("third_party", { held: v }),
                    "inv-tp-held")}
              </div>
              {tp.held === true && (
                <div className="mt-2 grid gap-2 md:grid-cols-2" data-testid="inv-tp-branch">
                  <div><Label>{t("inv6.thirdPartyName")}</Label>
                    <Input data-testid="inv-tp-name" defaultValue={tp.party || ""}
                            onChange={(e) => patchIn("third_party", { party: e.target.value })} /></div>
                  <div><Label>{t("inv6.confirmationLink")}</Label>
                    <Input data-testid="inv-tp-confirm" defaultValue={tp.confirmation_link || ""}
                            onChange={(e) => patchIn("third_party", { confirmation_link: e.target.value })} /></div>
                  <div className="col-span-2"><Label>{t("inv6.otherProcedures")}</Label>
                    <Textarea data-testid="inv-tp-other" defaultValue={tp.other_procedures_note || ""}
                                onChange={(e) => patchIn("third_party", { other_procedures_note: e.target.value })} /></div>
                </div>
              )}
            </section>

            {/* Attendance */}
            <section className="rounded border p-3" data-testid="inv-attendance">
              <div className="mb-1 text-sm font-semibold">{t("inv6.section.attendance")}</div>
              <div className="flex flex-wrap gap-2">
                {["attended", "unable_unforeseen", "impracticable"].map((m) => (
                  <button key={m} type="button"
                    data-testid={`inv-att-${m}`}
                    className={`rounded border px-3 py-1 ${att.mode === m ? "bg-blue-100 border-blue-500" : ""}`}
                    onClick={() => patchIn("attendance", { mode: m })}>
                    {t(`inv6.attendanceMode.${m}`)}
                  </button>
                ))}
              </div>
              {(att.mode === "unable_unforeseen" || att.mode === "impracticable") && (
                <div className="mt-2 space-y-1" data-testid="inv-att-branch">
                  <Label>{t("inv6.attendanceReason")}</Label>
                  <Input data-testid="inv-att-reason" defaultValue={att.reason || ""}
                          onChange={(e) => patchIn("attendance", { reason: e.target.value })} />
                  <Label>{t("inv6.altProcedures")}</Label>
                  <Textarea data-testid="inv-alt-procs" defaultValue={att.alternative_procedures_note || ""}
                             onChange={(e) => patchIn("attendance", { alternative_procedures_note: e.target.value })} />
                </div>
              )}
            </section>

            {/* Upload + sampling — N2 fix — styled button + hidden
                input + filename label so RTL never shows the browser's
                English "Choose File / No file chosen". */}
            <section className="rounded border p-3" data-testid="inv-listing">
              <div className="mb-1 text-sm font-semibold">{t("inv6.uploadListing")}</div>
              <div className="flex flex-wrap items-center gap-2">
                {/* Round 33 · C7 — open the picker programmatically (label→id
                    forwarding did not take the file on live). */}
                <Button size="sm" variant="outline" type="button"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="inv-upload-button">
                  {t("common.chooseFile")}
                </Button>
                <DownloadTemplateButton kind="inventory_count_listing"
                                          testid="inv-download-template" />
                <span className="text-xs text-slate-600"
                      data-testid="inv-upload-filename">
                  {doc.client_count_listing?.filename || t("common.noFileChosen")}
                </span>
              </div>
              <input id={`inv-upload-${ref}`} ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv"
                      className="hidden" onChange={upload} disabled={!!doc?.is_voided}
                      data-testid="inv-upload-file" />
              {(doc.client_count_listing?.file_count > 0 || doc.client_count_listing?.filename || asArray(doc.client_count_listings).some((l) => !l.deleted_at)) ? (
                <div className="mt-2 space-y-1" data-testid="inv-listing-combined">
                  {/* Round 8 · U1 — combined summary (replaces the old
                      single-file label). Totals are shown both ways
                      when there are unresolved duplicate flags. */}
                  <div className="text-xs text-emerald-700" data-testid="inv-listing-file">
                    <b>{doc.client_count_listing.file_count}</b>{" "}
                    {t("inv6.filesLabel") || "file(s)"} · {doc.client_count_listing.row_count} rows
                    {" · "}
                    <span data-testid="inv-listing-total">
                      {t("inv6.totalValue") || "Total value"}:{" "}
                      {doc.client_count_listing.value_mode === "n/a"
                          || doc.client_count_listing.total_value == null ? (
                        <span className="font-mono" data-testid="inv-listing-total-na">n/a</span>
                      ) : (
                        <span dir="ltr">
                          {fmtNumber(doc.client_count_listing.total_value)}
                        </span>
                      )}
                    </span>
                    {doc.client_count_listing.total_value != null
                      && doc.client_count_listing.unresolved_duplicates > 0
                      && doc.client_count_listing.total_value_resolved != null
                      && Number(doc.client_count_listing.total_value_resolved) !== Number(doc.client_count_listing.total_value) && (
                      <span className="ms-2 text-xs text-amber-800" data-testid="inv-listing-total-resolved">
                        · {t("inv6.totalResolved") || "excl. excluded dupes"}:{" "}
                        <span dir="ltr">{fmtNumber(doc.client_count_listing.total_value_resolved)}</span>
                      </span>
                    )}
                  </div>
                  {doc.client_count_listing.unresolved_duplicates > 0 && (
                    <div className="rounded border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900"
                         data-testid="inv-listing-dup-banner">
                      <div className="font-semibold">
                        {doc.client_count_listing.unresolved_duplicates}{" "}
                        {t("inv6.dupBanner") || "possible duplicate(s) across files (same SKU + Location)"}
                        {" · "}
                        <span dir="rtl">تكرارات محتملة بين الملفات (نفس الصنف والموقع)</span>
                      </div>
                      <div className="mt-0.5">
                        {t("inv6.dupBannerHelp") || "Rows are flagged yellow in the preview; auditor resolves each group."}
                      </div>
                    </div>
                  )}
                  {/* Round 9 · F4 — per-group resolve controls. One
                       card per duplicate_group: Keep both OR Exclude
                       row + mandatory reason. Resolved groups show
                       the resolution + who resolved it. */}
                  {asArray(doc.client_count_listing.duplicate_groups).map((g) => (
                    <DupGroupResolve key={g.key} group={g}
                                       entity="inventory-count"
                                       engId={currentEngId} ref_={ref}
                                       onResolved={async () => {
                                         const fresh = await api.get(`/engagements/${currentEngId}/inventory-count/ref/${ref}`);
                                         setDoc(fresh.data);
                                       }} />
                  ))}
                  {(doc.client_count_listing.value_mode === "n/a"
                      || doc.client_count_listing.total_value == null) && (
                    <div className="text-[11px] text-amber-800"
                         data-testid="inv-listing-value-na-note">
                      {t("inv6.valueNote")}
                    </div>
                  )}
                  <ul className="divide-y rounded border bg-white dark:bg-slate-900" data-testid="inv-listing-files">
                    {!asArray(doc.client_count_listings).some((l) => !l.deleted_at) && doc.client_count_listing?.filename && (
                      <li className="flex items-center justify-between gap-2 px-2 py-1 text-[11px]" data-testid="inv-listing-row-legacy">
                        <div className="min-w-0 flex-1 break-words">
                          <span className="font-mono">{doc.client_count_listing.filename}</span>
                          <span className="ms-2 text-muted-foreground">
                            {doc.client_count_listing.row_count ?? (doc.client_count_listing.rows || []).length} rows{" · "}
                            {fmtNumber(doc.client_count_listing.total_value ?? asArray(doc.client_count_listing.rows).reduce((a, r) => a + (Number(r.value) || 0), 0))}
                            {doc.client_count_listing.uploaded_at ? ` · ${fmtDateTime(doc.client_count_listing.uploaded_at)}` : ""}
                            {doc.client_count_listing.uploaded_by_name ? ` · ${doc.client_count_listing.uploaded_by_name}` : ""}
                            {" · "}{t("inv6.parsed") || "Parsed"}
                          </span>
                        </div>
                        <Button size="sm" variant="ghost" className="h-6 text-[11px]" data-testid="inv-listing-view-legacy"
                                onClick={() => setViewRows(doc.client_count_listing.rows || [])}>
                          {t("inv6.viewRows") || "View rows"}
                        </Button>
                      </li>
                    )}
                    {!asArray(doc.client_count_listings).some((l) => !l.deleted_at) && !doc.client_count_listing?.filename && (
                      <li className="px-2 py-2 text-[11px] text-muted-foreground" data-testid="inv-listing-empty">
                        {t("inv6.noFile") || "No file uploaded yet — upload the count sheet"}
                      </li>
                    )}
                    {asArray(doc.client_count_listings).filter((l) => !l.deleted_at).map((l) => (
                      <li key={l.id} className="flex items-center justify-between gap-2 px-2 py-1 text-[11px]"
                          data-testid={`inv-listing-row-${l.id}`}>
                        <div className="min-w-0 flex-1 break-words">
                          <span className="font-mono">{l.filename}</span>
                          <span className="ms-2 text-muted-foreground">
                            {l.row_count} rows{" · "}
                            {l.total_value == null ? "n/a" : fmtNumber(l.total_value)}
                            {" · "}{fmtDate(l.uploaded_at)}
                            {l.uploaded_by_name ? ` · ${l.uploaded_by_name}` : ""}
                          </span>
                        </div>
                        <Button size="sm" variant="ghost" className="h-6 text-[11px]" data-testid={`inv-listing-view-${l.id}`}
                                onClick={() => setViewRows(l.rows || [])}>{t("inv6.viewRows") || "View rows"}</Button>
                        <Button size="sm" variant="ghost" className="h-6 text-[11px] text-red-700"
                                data-testid={`inv-listing-remove-${l.id}`}
                                onClick={async () => {
                                  if (!await askConfirm(t("inv6.removeFileConfirm") || "Remove this file? It will go to the recycle bin.")) return;
                                  try {
                                    await api.delete(`/engagements/${currentEngId}/inventory-count/ref/${ref}/listings/${l.id}`);
                                    const fresh = await api.get(`/engagements/${currentEngId}/inventory-count/ref/${ref}`);
                                    setDoc(fresh.data);
                                    toast.success(t("inv6.removedOk") || "File moved to recycle bin");
                                  } catch (e) { toast.error(toErrMsg(e, "Remove failed")); }
                                }}>
                          {t("common.remove") || "Remove"}
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="mt-1 text-xs text-amber-700" data-testid="inv-listing-none">—</div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" data-testid="inv-send-sampling"
                  disabled={!doc.client_count_listing?.document_id
                            || Number(doc.client_count_listing?.row_count || 0) === 0}
                  onClick={async () => {
                    try { await api.post(`/engagements/${currentEngId}/inventory-count/ref/${ref}/send-to-sampling`);
                      toast.success(t("inv6.sendToSamplingOk"));
                    } catch (e) { toast.error(toErrMsg(e, "Send failed")); }
                  }}>{t("inv6.sendToSampling")}</Button>
                {doc.client_count_listing?.document_id
                  && Number(doc.client_count_listing?.row_count || 0) === 0 && (
                  <span className="text-xs text-amber-800"
                        data-testid="inv-send-sampling-zero-rows">
                    {t("headFolders.zeroRows")}
                  </span>
                )}
                {!doc.client_count_listing?.document_id && (
                  <span className="text-xs text-amber-800"
                        data-testid="inv-send-sampling-reason">
                    {t("inv6.uploadListingFirst") || "Upload the client count listing first."}
                  </span>
                )}
              </div>
            </section>

            {/* Sign-off */}
            <section className="rounded border p-3" data-testid="inv-signoff">
              <div className="grid gap-2 md:grid-cols-2">
                <div><Label>{t("inv6.conclusion")}</Label>
                  <Textarea data-testid="inv-conclusion" defaultValue={dv.conclusion || ""}
                    onChange={(e) => draftSet("conclusion", e.target.value)} /></div>
              </div>
              {!gate.ok && (
                <div className="mt-1 text-xs text-red-700" data-testid="inv-guard-reasons">
                  {gate.reasons.map((r) => t(`inv6.reasons.${r}`) || r).join(" · ")}
                </div>
              )}
              <Button className="mt-2" data-testid="inv-mark-complete"
                disabled={(!gate.ok && !doc.completed) || fd.dirty}
                onClick={() => patch({ completed: !doc.completed })}>
                {doc.completed ? "Unmark" : t("inv6.markComplete")}
              </Button>
            </section>
            <SaveCancelBar dirty={fd.dirty} saving={saving} onSave={saveForm} onCancel={cancelForm} lang={lang} testid="inv-save-bar" />
          </div>
        )}
      <Dialog open={viewRows != null} onOpenChange={(o) => { if (!o) setViewRows(null); }}>
        <DialogContent className="h-[92vh] max-w-[96vw] overflow-auto p-4" data-testid="inv-listing-rows-viewer">
          <ResizableTable cols={listCols} className="w-full text-[11px]">
            <thead className="border-b bg-slate-50 text-[10px] uppercase text-muted-foreground">
              <tr><ResizableTh index={0} className="p-1 text-start">#</ResizableTh><ResizableTh index={1} className="p-1 text-start">{t("b3.item")}</ResizableTh><ResizableTh index={2} className="p-1 text-start">{t("b3.description")}</ResizableTh><ResizableTh index={3} className="p-1 text-start">{t("b3.location")}</ResizableTh><ResizableTh index={4} className="p-1 text-end">{t("b3.qty")}</ResizableTh><ResizableTh index={5} className="p-1 text-end">{t("b3.unitCost")}</ResizableTh><ResizableTh index={6} className="p-1 text-end">{t("b3.value")}</ResizableTh></tr>
            </thead>
            <tbody className="divide-y">
              {asArray(viewRows).map((r, i) => (
                <tr key={r.id || i}><td className="p-1">{i + 1}</td><td className="p-1 font-mono">{r.item}</td><td className="p-1">{r.description}</td><td className="p-1">{r.location}</td>
                  <td className="p-1 text-end" dir="ltr">{r.quantity}</td><td className="p-1 text-end" dir="ltr">{r.unit_cost == null || r.unit_cost === "" ? "" : fmtNumber(r.unit_cost)}</td><td className="p-1 text-end" dir="ltr">{fmtNumber(r.value || 0)}</td></tr>
              ))}
            </tbody>
          </ResizableTable>
          <div className="mt-2 text-end text-[11px] font-semibold" data-testid="inv-listing-rows-total">
            {(viewRows || []).length} rows · {fmtNumber(asArray(viewRows).reduce((a, r) => a + (Number(r.value) || 0), 0))}
          </div>
        </DialogContent>
      </Dialog>
    </FullScreenDialog>
  );
}

function TestCountsTable({ rows, kind, t, testidPrefix }) {
  const countCols = useResizableColumns({ storageKey: `inv-${kind}`, defaults: [150, 140, 110, 110, 100, 110, 130], mins: [100, 96, 90, 90, 80, 90, 100] });
  return (
    <div className="mt-2" data-testid={`inv-${kind}-table`}>
      <div className="text-xs font-medium">
        {kind === "sheet_to_floor" ? t("inv6.sheetToFloor") : t("inv6.floorToSheet")}
      </div>
      <div className="overflow-auto rounded border">
        <ResizableTable cols={countCols} className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr>
              <ResizableTh index={0} className="p-1 text-start">{t("inv6.testColItem")}</ResizableTh>
              <ResizableTh index={1} className="p-1 text-start">{t("inv6.testColLocation")}</ResizableTh>
              <ResizableTh index={2} className="p-1 text-end">{t("inv6.listingQty")}</ResizableTh>
              <ResizableTh index={3} className="p-1 text-end">{t("inv6.countedQty")}</ResizableTh>
              <ResizableTh index={4} className="p-1 text-end">{t("inv6.diff")}</ResizableTh>
              <ResizableTh index={5} className="p-1 text-end">{t("inv6.unitCost")}</ResizableTh>
              <ResizableTh index={6} className="p-1 text-end">{t("inv6.valueDiff")}</ResizableTh>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="p-2 text-center text-muted-foreground"
                        data-testid={`${testidPrefix}-empty`}>—</td></tr>
            )}
            {rows.map((r) => {
              const listing = Number(r.listing_qty ?? 0);
              const counted = Number(r.counted_qty ?? 0);
              const diff = counted - listing;
              const val = diff * Number(r.unit_cost ?? 0);
              return (
                <tr key={r.id || `${r.item}-${r.location}`} data-testid={`${testidPrefix}-row-${r.id || r.item}`}>
                  <td className="p-1">{r.item}</td>
                  <td className="p-1">{r.location}</td>
                  <td className="p-1 text-end" dir="ltr">{r.listing_qty ?? "—"}</td>
                  <td className="p-1 text-end" dir="ltr">{r.counted_qty ?? "—"}</td>
                  <td className="p-1 text-end" dir="ltr">{diff}</td>
                  <td className="p-1 text-end" dir="ltr">{r.unit_cost ?? "—"}</td>
                  <td className="p-1 text-end" dir="ltr">{fmtNumber(val)}</td>
                </tr>
              );
            })}
          </tbody>
        </ResizableTable>
      </div>
    </div>
  );
}
